import { FC, useContext, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MobXProviderContext } from "mobx-react";
import { Controller, useForm } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { AsyncProfileImageInDataTableRow } from "./AsyncProfileImageInDataTableRow";
import { Toast } from "primereact/toast";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_CAREGIVER } from "../../../constants/RegisterConstants";
import {
  appAccentPanelStyle,
  appDataTableWrapperStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appWideDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

interface LinkedLookupModalProps {
  homeId: string | undefined;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  lookUpType?: string;
}

const LinkedLookupModal: FC<LinkedLookupModalProps> = ({
  homeId,
  isOpen,
  toggleModal,
  flag,
  setFlag,
  lookUpType,
}) => {
  const toast = useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const defaultValues = {
    email: '',
  }

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onSearchSubmit = async (data: any) => {
    setErrorMessage(null);
    setLoading(true);
    try {
      await backendAPIStore.init();
      const searchResponse = await backendAPIStore.searchUsers(data.email.toLowerCase(), homeId!);
      const caregiversOnly = searchResponse.filter(user => user.userRoleType === ROLE_TYPE_CAREGIVER);
      setSearchResults(caregiversOnly);
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const onLinkSubmit = async (data: any) => {
    try {
      setLoading(true);
      await backendAPIStore.init();
      const requestBody = {
        homeId: homeId,
        userId: data.userId,
      }
      await backendAPIStore.addHomeLink(requestBody);
      setFlag(!flag);
      toggleModal(false);
    } catch (error: any) {
      setErrorMessage(error.message);
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error.message, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const headerElement = renderSurfaceHeader(
    "Link A Caregiver",
    "Search by exact email address and attach the caregiver to this home.",
    "pi pi-link",
  );

  const resultItemTemplate = (rowData) => {
    return (
      <div className="grid d-flex flex-wrap justify-content-center align-items-center">
        <div className="col col flex-wrap flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
          <div><AsyncProfileImageInDataTableRow userId={rowData.userId} homeId={homeId} roleType={rowData.userRoleType} /></div>
          <div>{rowData.firstName}&nbsp;{rowData.lastName}</div>
          <div>{rowData.email}</div>
        </div>
        <div className="col flex-wrap flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", textTransform: "capitalize" }}>{rowData.userRoleType}</div>
        <div className="text-500 text-sm col flex-wrap flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
          <Button icon="pi pi-link" rounded label="Link" severity="warning" className="p-button-outlined" onClick={(e) => { e.preventDefault(); onLinkSubmit(rowData) }} />
        </div>
      </div>
    )
  };

  const resultTableHeaderTemplate = (
    <div>Matched Results: {searchResults.length}</div>
  );

  return (
    <Dialog visible={isOpen} modal header={headerElement} style={appWideDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <form>
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Search guidance</div>
          <div className="mt-2" style={appMutedTextStyle}>
            Use the caregiver's exact email address to find eligible matches and link them to this home.
          </div>
        </div>
        <div style={appFormSectionStyle} className="grid">
          <div className="field col">
            <span className="p-float-label">
              <Controller name="email" control={control} rules={{
                pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address.' }
              }} render={({ field, fieldState }) => (
                <InputText type="email" placeholder="Enter exact email address" id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
              <label htmlFor="email" className={classNames({ 'p-error': errors.email })}>Email</label>
            </span>
            {getFormErrorMessage('email')}
          </div>
        </div>

        <Button type="submit" label="Search" severity="warning" rounded className="mb-2 p-button-outlined" loading={loading} onClick={handleSubmit(onSearchSubmit)} />

        {
          searchResults.length === 0 && !loading && (
            <div>No match found</div>
          )
        }
        {searchResults.length > 0 && (
          <div style={appDataTableWrapperStyle}>
            <DataTable value={searchResults} header={resultTableHeaderTemplate} showHeaders={false}>
              <Column header="" body={resultItemTemplate}></Column>
            </DataTable>
          </div>
        )}
      </form>
    </Dialog>
  );
};

export default LinkedLookupModal;
